import { useContext, useState } from "react";
import PropTypes from "prop-types";
import { useFormState, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

import VStack from "shared/components/layout/stack/VStack";
import Form from "shared/components/inputs/form/Form";
import ModalActions from "shared/components/feedback/modals/ModalActions";
import Dropdown from "shared/components/inputs/form-fields/Dropdown";

import { sendNotification } from "shared/components/feedback/notification/notification";
import FormFeedbackNotificationText from "shared/components/feedback/error/FormFeedbackNotificationText";
import TextArea from "shared/components/inputs/form-fields/TextArea";
import { getDropdownOptionsNoDefaultVal } from "shared/helper-functions/dropdown/dropdownHelpers";

import ProjectsDashboardLayoutContext from "features/projects/contexts/ProjectsDashboardLayoutContext";

import useProjectsDashboardNotes, {
    useProjectByIdDetails,
    useProjectsNoteTypes,
} from "features/projects/project-dashboard/single/useProjectsDashboardNotes";

const ModalCTA = ({
    isEditing,
    handleOnClickDeleteNote,
    isLoading,
    onClose,
}) => {
    const { isValid } = useFormState();
    const formData = useWatch();

    return (
        <ModalActions
            primaryAction={{
                type: "submit",
                label: isEditing ? "Save" : "Add Note",
                leadingIcon: isEditing ? "save" : null,
                disabled: !isValid,
            }}
            secondaryAction={{
                type: "button",
                label: "Cancel",
                onClick: onClose,
            }}
            {...(isEditing && {
                tertiaryAction: {
                    type: "button",
                    label: "Delete",
                    onClick: () => handleOnClickDeleteNote(formData),
                },
            })}
            as="fieldset"
            disabled={isLoading}
        />
    );
};

function transformProjectsNoteDataToFormData(noteData = {}) {
    return {
        noteType: noteData.TypeId,
        note: noteData.Text,
    };
}

function transformFormDataToProjectsNoteData(
    formData = {},
    ProjectsNoteData = {}
) {
    const newProjectsNoteData = { ...ProjectsNoteData };
    newProjectsNoteData.TypeId = formData.noteType;
    newProjectsNoteData.Content = formData.note;

    return newProjectsNoteData;
}

const ProjectsDashboardNotesForm = ({ noteIdToEdit, onClose }) => {
    const { projectId } = useParams();
    const { setFlagToLoad } = useContext(ProjectsDashboardLayoutContext);
    const { projectByIdDetails } = useProjectByIdDetails();
    const {
        useProjectsNotesList,
        createProjectDashboardNote,
        updateProjectDashboardNote,
        deleteProjectDashboardNote,
    } = useProjectsDashboardNotes();
    const notesList = useProjectsNotesList({
        projectIdFE: projectId,
        noteTypeId: "",
    });
    const { projectsNoteTypes } = useProjectsNoteTypes();
    const [inProgress, setInProgress] = useState(false);

    const currentProjectsNoteData = notesList?.data?.find(
        (note) => note.Id === noteIdToEdit
    );

    if (
        notesList.isLoading ||
        projectsNoteTypes.isLoading ||
        projectByIdDetails.isLoading
    ) {
        return <>Loading...</>;
    }

    if (
        notesList.isError ||
        projectsNoteTypes.isError ||
        projectByIdDetails.isError
    ) {
        return <>Error</>;
    }

    const isEditing = noteIdToEdit !== undefined;
    const modifier = isEditing
        ? updateProjectDashboardNote
        : createProjectDashboardNote;

    if (isEditing && !currentProjectsNoteData) {
        return <>Could not find note</>;
    }

    const getNameOfNotification = (res) => {
        return res ? `A note on ${projectByIdDetails?.data?.Name}` : "note";
    };

    const handleOnClickDeleteNote = async (prevData) => {
        const response = await deleteProjectDashboardNote.mutateAsync({ noteId: noteIdToEdit });
        onClose();
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isDeleting={true}
                name={getNameOfNotification(response?.status === 200)}
            />,
            {
                close: true,
                action: response?.status === 200,
                actionProps: response?.status === 200
                    ? {
                          label: "Undo",
                          onClick: () =>
                              onSubmit({
                                  ...prevData,
                                  isNoteRemoved: true,
                              }),
                      }
                    : null,
            }
        );
    };

    const onSubmit = async (formData) => {
        const data = transformFormDataToProjectsNoteData(
            formData,
            currentProjectsNoteData
        );
        const newModifier = formData.isNoteRemoved
            ? createProjectDashboardNote
            : modifier;
        const allData = {
            noteId: noteIdToEdit,
            body: data,
        };
        const response = await newModifier.mutateAsync(allData);
        onClose();
        setInProgress(false);
        isEditing && setFlagToLoad(true);
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isEditing={formData.isNoteRemoved ? false : isEditing}
                name={getNameOfNotification(response?.status === 200)}
            />
        );
    };

    const noteTypesOptions = projectsNoteTypes.isLoading
        ? []
        : getDropdownOptionsNoDefaultVal(projectsNoteTypes?.data);

    return (
        <Form
            onSubmit={onSubmit}
            defaultValues={transformProjectsNoteDataToFormData(
                currentProjectsNoteData
            )}
            mode="onChange"
        >
            <VStack spacing="Three">
                <VStack>
                    <Dropdown
                        name="noteType"
                        placeholder="Select"
                        validations={{
                            required: true,
                        }}
                        label="Note Type"
                        itemsAreLoading={projectsNoteTypes.isLoading}
                        options={noteTypesOptions}
                    />
                    <TextArea
                        name="note"
                        showClearIcon={true}
                        characterCounter={true}
                        label="Note"
                        placeholder="Add a note to your project"
                        validations={{
                            required: true,
                        }}
                    />
                </VStack>

                <ModalCTA
                    isEditing={isEditing}
                    handleOnClickDeleteNote={handleOnClickDeleteNote}
                    onClose={onClose}
                    isLoading={modifier?.isLoading || inProgress}
                />
            </VStack>
        </Form>
    );
};

ModalCTA.propTypes = {
    isEditing: PropTypes.bool,
    handleOnClickDeleteNote: PropTypes.func,
    isLoading: PropTypes.bool,
    onClose: PropTypes.func.isRequired
};

ProjectsDashboardNotesForm.propTypes = {
    noteIdToEdit: PropTypes.string,
    onClose: PropTypes.func.isRequired
};

export default ProjectsDashboardNotesForm;
